import { AfterViewInit, Component, OnInit, viewChild, inject, ChangeDetectionStrategy } from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MasterDataService} from '../../master-data/master-data-service';
import {ActivatedRoute, Router} from '@angular/router';
import {ItemEditComponent} from '../../item-edit/item-edit.component';
import {AllowedStatusesEditComponent} from '../allowed-statuses-edit/allowed-statuses-edit.component';
import {ConfirmDialogComponent} from '../../shared/confirm-dialog/confirm-dialog.component';
import {ConfDialogType, ConfirmDialogModel} from '../../shared/confirm-dialog/confim-dialog.model';
import {CONFIRM_DIALOG_ALLOWED_STATUS_DELETE, CONFIRM_DIALOG_CONFIRM_ACTION, OrderStates} from '../../../locale/consts.en';
import {finalize, first} from 'rxjs/operators';
import {OrderState, Status, StatusIcon, MasterData, OrderType} from '../../master-data/master-data.model';
import {MatSelectChange} from '@angular/material/select';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {TranslationDialogService} from 'src/app/shared/translation-dialog/translation-dialog.service';


@Component({
    selector: 'app-statuses',
    templateUrl: './statuses.component.html',
    styleUrls: ['./statuses.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StatusesComponent implements OnInit, AfterViewInit {
  private dialog = inject(MatDialog);
  private masterDataService = inject(MasterDataService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  readonly translationDialogService = inject(TranslationDialogService);


  readonly sort = viewChild(MatSort);
  dataSource: MatTableDataSource<Status>;
  displayedColumns: string[] = ['name', 'externalId', 'metaId', 'nextStatuses', 'add', 'edit', 'orderState', 'icon', 'color', 'translations'];
  public color: string;
  statuses: Status[] = [];
  statusIcons: StatusIcon[];
  presetColors: string[] = [
    '#000000',
    '#808080',
    '#FF0000',
    '#800080',
    '#008000',
    '#FFFF00',
    '#ffa500',
    '#0000FF',
    '#D2691E',
    '#7FFFD4',
    '#8A2BE2',
    '#F0E68C'
  ];

  orderStates = OrderStates;

  compareFn(f1: StatusIcon, f2: StatusIcon): boolean {
    return f1 && f2 ? f1.name === f2.name : f1 === f2;
  }

  ngOnInit(): void {
    this.statuses = [...this.route.snapshot.data.statuses];
    this.statuses.sort((a, b) => (a.metaId > b.metaId) ? 1 : ((b.metaId > a.metaId) ? -1 : 0));
    this.dataSource = new MatTableDataSource(this.statuses);
    this.loadStatusIcons();
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  loadStatusIcons() {
    this.masterDataService.getStatusIcons()
      .subscribe((icons) => {
        this.statusIcons = icons;
      });
  }

  updateStatusIcon(s: Status, event: MatSelectChange) {
    const status = new Status();
    Object.assign(status, s);
    status.icon = event.value as StatusIcon;
    console.log('update to api', s.name, '  ', s.icon?.name, ' ikona ', event.value.name);
    this.saveStatusToApi(status);
  }

  updateOrderState(s: Status, event: MatSelectChange) {
    const status = new Status();
    Object.assign(status, s);
    status.orderState = event.value as OrderState;
    console.log('update to api', s.name, ' OLD ', s.orderState, 'order state NEW ', status.orderState);
    this.saveStatusToApi(status);
  }

  saveStatusToApi(status: Status) {
    this.masterDataService.updateStatus(status)
      .pipe(
        finalize(() => {
          this.masterDataService.getStatuses()
            .pipe(
              first(),
            )
            .subscribe((statuses) => {
              this.statuses = statuses.sort((a, b) => (a.metaId > b.metaId) ? 1 : ((b.metaId > a.metaId) ? -1 : 0));
              this.dataSource.data = this.statuses
            });
        })
      )
      .subscribe({
        next: (response) => {
          this.masterDataService.handleResponse(response);
        },
        error: () => this.masterDataService.savingErrorMessage()
      });
  }

  updateStatus(s: Status) {
    const status = new Status();
    Object.assign(status, s);
    const dialogRef = this.dialog.open(ItemEditComponent, {
        width: '20vw',
        data: status
      }
    );
    dialogRef.afterClosed().subscribe(async (data: Status) => {
      if (data != null) {
        status.name = data.name;
        status.externalId = data.externalId;
        status.metaId = data.metaId;
        this.saveStatusToApi(status);
      }
    });
  }

  updateStatusIconColor(s: Status) {
    const status = new Status();
    Object.assign(status, s);
    this.saveStatusToApi(status);
  }

  onAddStatus() {
    const status = new Status();
    const dialogRef = this.dialog.open(ItemEditComponent, {
        width: '20vw',
        data: status
      }
    );
    dialogRef.afterClosed().subscribe(async (data: Status) => {
      if (data != null) {
        this.saveStatusToApi(data);
      }
    });
  }

  openTranslationsDialog(item: MasterData): void {
    this.translationDialogService.open(item, 'Status').subscribe((result) => {
      if (result) {
        item.translations = result;
        this.saveStatusToApi(item as Status);
      }
    });
  }


  onBack() {
    this.router.navigate(['/']).then();
  }


  addNextStatus(status: Status) {
    const origStatus = status;
    const availableStatuses = this.statuses.filter((s) => status.allowedStatuses.findIndex((as) => as.uuid === s.uuid) === -1)
      .filter(s => s.uuid !== status.uuid);
    const dialogRef = this.dialog.open(AllowedStatusesEditComponent, {
        width: '20vw',
        data: availableStatuses
      }
    );
    dialogRef.afterClosed().subscribe(async (data: Status) => {
      if (data != null) {
        origStatus.allowedStatuses.push(data);
        this.saveStatusToApi(status);
      }
    });
  }

  deleteAllowedStatus(status: Status, i: number) {
    const dialogData = new ConfirmDialogModel(CONFIRM_DIALOG_CONFIRM_ACTION + '  ' + status.name
      , CONFIRM_DIALOG_ALLOWED_STATUS_DELETE + '   ' + status.allowedStatuses[i].name, ConfDialogType.CONFIRM);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: '400px',
      data: dialogData
    });

    dialogRef.afterClosed().subscribe(data => {
      if (data) {
        status.allowedStatuses.splice(i, 1);
        console.log('Deleting allowed status', status);
        this.saveStatusToApi(status);
      }
    });
  }


}
